import { getCachedVenueRecord } from './venueRecordCache';

/** Stripe payment fee constants — keep aligned with database-api config. */
export const PLATFORM_FEE_GBP = 6;
/** 2.9% + 30p — gross-up formula: (subtotal + platform + fixed) / (1 - rate). */
export const PROCESSING_FEE_BPS = 290;
export const PROCESSING_FEE_FIXED_GBP = 0.3;

const parseIdList = (raw, fallback = []) => {
  if (raw == null || String(raw).trim() === '') {
    return [...fallback];
  }
  return String(raw)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
};

/** Platform bookings at these venues have no Occasia platform fee (processing fee unchanged). */
export const VENUES_ZERO_PLATFORM_FEE_IDS = parseIdList(
  process.env.REACT_APP_VENUES_ZERO_PLATFORM_FEE_IDS,
  [
    '660e8400-e29b-41d4-a716-446655440007',
    '660e8400-e29b-41d4-a716-446655440023'
  ]
);

export const roundMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
};

export const isManualBookingSource = (bookingOrSource) => {
  if (bookingOrSource == null) return false;
  if (typeof bookingOrSource === 'string') {
    const source = bookingOrSource.toLowerCase();
    return source === 'manual' || source === 'venue_dashboard';
  }
  const req = bookingOrSource.requirements || {};
  const source = String(
    bookingOrSource.booking_source ?? req.source ?? ''
  ).toLowerCase();
  return source === 'manual' || source === 'venue_dashboard';
};

export const resolvePlatformFeeGbp = ({
  venueId,
  isPlatformBooking = true,
  platformFeeEnabled
} = {}) => {
  if (!isPlatformBooking) return 0;
  if (platformFeeEnabled === false) return 0;

  const id = venueId != null ? String(venueId).trim() : '';
  if (platformFeeEnabled === undefined && id) {
    const cachedVenue = getCachedVenueRecord(id);
    if (cachedVenue?.platform_fee_enabled === false) return 0;
  }

  if (platformFeeEnabled === true) return PLATFORM_FEE_GBP;
  if (platformFeeEnabled === undefined && id && VENUES_ZERO_PLATFORM_FEE_IDS.includes(id)) {
    return 0;
  }
  return PLATFORM_FEE_GBP;
};

/**
 * Gross-up charge so subtotal + platform fee is covered after Stripe processing fees.
 * Mirrors backend gross_up_charge_minor().
 */
export const grossUpChargeTotal = (
  subtotal,
  {
    platformFee = 0,
    rateBps = PROCESSING_FEE_BPS,
    fixedFee = PROCESSING_FEE_FIXED_GBP
  } = {}
) => {
  const net = roundMoney(Number(subtotal) + Number(platformFee));
  if (net <= 0) return 0;
  const rate = Math.max(0, Math.min(rateBps, 9999)) / 10_000;
  const fixedMinor = Math.round(Number(fixedFee) * 100);
  const netMinor = Math.round(net * 100);
  if (rate >= 1) {
    return roundMoney((netMinor + fixedMinor) / 100);
  }
  const chargeMinor = Math.ceil((netMinor + fixedMinor) / (1 - rate));
  return roundMoney(chargeMinor / 100);
};

/**
 * Full organiser-facing breakdown for Stripe checkout.
 */
export const computePaymentFeeBreakdown = (
  subtotal,
  { isPlatformBooking = true, venueId, platformFeeEnabled } = {}
) => {
  const sub = roundMoney(subtotal);
  const platformFee = resolvePlatformFeeGbp({ venueId, isPlatformBooking, platformFeeEnabled });
  const chargeTotal = grossUpChargeTotal(sub, { platformFee });
  const processingFee = roundMoney(chargeTotal - sub - platformFee);

  return {
    subtotal: sub,
    platformFee,
    processingFee: Math.max(0, processingFee),
    chargeTotal,
    isPlatformBooking: Boolean(isPlatformBooking)
  };
};

export const computePaymentFeeBreakdownFromBooking = (booking) => {
  const subtotal = Number(booking?.subtotal ?? booking?.final_amount ?? booking?.amount_pending ?? 0);
  return computePaymentFeeBreakdown(subtotal, {
    isPlatformBooking: !isManualBookingSource(booking),
    venueId: booking?.venueId ?? booking?.venue_id,
  });
};

/**
 * Merge API eligibility/status payload with client-side fallback.
 * When a charge total is persisted, it is authoritative — fees are not recalculated.
 */
export const resolvePaymentBreakdown = (payload, fallbackSubtotal = 0) => {
  if (!payload || typeof payload !== 'object') {
    return computePaymentFeeBreakdown(fallbackSubtotal, {
      isPlatformBooking: true,
      venueId: null,
    });
  }

  const chargeTotal = roundMoney(
    payload.charge_total ??
      payload.amount ??
      payload.final_amount ??
      payload.total_amount
  );
  const storedSubtotal = roundMoney(payload.subtotal ?? payload.booking_amount);
  const storedProcessing = roundMoney(payload.processing_fee);
  const storedPlatform = roundMoney(payload.platform_fee ?? 0);

  if (chargeTotal != null && chargeTotal > 0 && storedSubtotal != null && storedSubtotal > 0) {
    const processingFee =
      storedProcessing != null && storedProcessing >= 0
        ? storedProcessing
        : Math.max(0, roundMoney(chargeTotal - storedSubtotal - storedPlatform));
    return {
      subtotal: storedSubtotal,
      platformFee: storedPlatform,
      processingFee,
      chargeTotal,
      isPlatformBooking: Boolean(payload.is_platform_booking)
    };
  }

  if (chargeTotal != null && chargeTotal > 0) {
    const processingFee =
      storedProcessing != null && storedProcessing >= 0
        ? storedProcessing
        : Math.max(0, roundMoney(chargeTotal - (storedSubtotal ?? 0) - storedPlatform));
    return {
      subtotal: storedSubtotal ?? Math.max(0, roundMoney(chargeTotal - storedPlatform - processingFee)),
      platformFee: storedPlatform,
      processingFee,
      chargeTotal,
      isPlatformBooking: Boolean(payload.is_platform_booking)
    };
  }

  const hasApiBreakdown =
    payload.charge_total != null &&
    payload.subtotal != null &&
    payload.processing_fee != null;

  if (hasApiBreakdown) {
    const subtotal = roundMoney(payload.subtotal);
    const platformFee = roundMoney(payload.platform_fee ?? 0);
    const processingFee = roundMoney(payload.processing_fee);
    const apiChargeTotal = roundMoney(payload.charge_total);

    // API sometimes treats charge total as subtotal when final_amount already includes fees.
    if (
      processingFee > 0 &&
      Math.abs(subtotal - apiChargeTotal) < 0.02 &&
      apiChargeTotal > processingFee + platformFee + 0.001
    ) {
      const correctedSubtotal = roundMoney(apiChargeTotal - platformFee - processingFee);
      return {
        subtotal: correctedSubtotal,
        platformFee,
        processingFee,
        chargeTotal: apiChargeTotal,
        isPlatformBooking: Boolean(payload.is_platform_booking)
      };
    }

    return {
      subtotal,
      platformFee,
      processingFee,
      chargeTotal: apiChargeTotal,
      isPlatformBooking: Boolean(payload.is_platform_booking)
    };
  }

  const subtotal = roundMoney(
    payload.subtotal ??
      payload.booking_amount ??
      fallbackSubtotal
  );
  return computePaymentFeeBreakdown(subtotal, {
    isPlatformBooking: payload.is_platform_booking != null
      ? Boolean(payload.is_platform_booking)
      : !isManualBookingSource(payload),
    venueId: payload.venue_id ?? payload.venueId,
  });
};

export const formatGbp = (amount) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(Number.isFinite(Number(amount)) ? Number(amount) : 0);

const parseSnapshotAmount = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  const parsed = Number.parseFloat(String(value).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
};

/** Snapshot stored on booking create/update (and shown in the change log). */
export const buildFeeBreakdownSnapshot = ({
  spaceFee = 0,
  addOnsTotal = 0,
  discountAmount = 0,
  chargeableVenue = null,
  isPlatformBooking = true,
  venueId,
  spaceDetail = null
} = {}) => {
  const space = roundMoney(spaceFee);
  const addOns = roundMoney(addOnsTotal);
  const discount = roundMoney(discountAmount);
  const chargeable =
    chargeableVenue != null
      ? roundMoney(chargeableVenue)
      : roundMoney(Math.max(0, space + addOns - discount));
  if (chargeable <= 0) {
    return {
      space_fee: space,
      add_ons: addOns,
      discount,
      platform_fee: 0,
      processing_fee: 0,
      total_payable: 0,
      space_detail: spaceDetail || null
    };
  }
  const fees = computePaymentFeeBreakdown(chargeable, { isPlatformBooking, venueId });
  return {
    space_fee: space,
    add_ons: addOns,
    discount,
    platform_fee: fees.platformFee,
    processing_fee: fees.processingFee,
    total_payable: fees.chargeTotal,
    space_detail: spaceDetail || null
  };
};

export const extractFeeBreakdownSnapshot = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  const nested = payload.fee_breakdown;
  if (nested && typeof nested === 'object' && (nested.total_payable != null || nested.space_fee != null)) {
    return {
      space_fee: roundMoney(nested.space_fee),
      add_ons: roundMoney(nested.add_ons),
      discount: roundMoney(nested.discount),
      platform_fee: roundMoney(nested.platform_fee),
      processing_fee: roundMoney(nested.processing_fee),
      total_payable: roundMoney(nested.total_payable),
      space_detail: nested.space_detail || null
    };
  }
  const req =
    payload.requirements && typeof payload.requirements === 'object' ? payload.requirements : {};
  const space = parseSnapshotAmount(req.base_fee ?? payload.base_fee);
  const addOns = parseSnapshotAmount(req.add_ons_total ?? payload.add_ons_total) ?? 0;
  const discount = parseSnapshotAmount(req.discount_amount ?? payload.discount_amount) ?? 0;
  const platform = parseSnapshotAmount(req.platform_fee ?? payload.platform_fee) ?? 0;
  const processing = parseSnapshotAmount(req.processing_fee ?? payload.processing_fee);
  const total =
    parseSnapshotAmount(payload.final_amount) ??
    parseSnapshotAmount(payload.total_amount) ??
    parseSnapshotAmount(req.booking_amount);
  if (space == null && total == null && addOns <= 0) return null;

  const spaceFee = space ?? 0;
  let totalPayable = total;
  let processingFee = processing;
  if (totalPayable == null) {
    totalPayable = roundMoney(spaceFee + addOns - discount + platform + (processingFee || 0));
  }
  if (processingFee == null) {
    processingFee = Math.max(0, roundMoney(totalPayable - spaceFee - addOns + discount - platform));
  }
  const dur = parseSnapshotAmount(req.duration_hours ?? payload.duration_hours);
  const headcount = parseSnapshotAmount(payload.headcount ?? req.headcount);
  const perPerson = parseSnapshotAmount(req.per_person_rate);
  const hourly = parseSnapshotAmount(req.space_hourly_rate);
  let spaceDetail = null;
    if (perPerson != null && perPerson > 0 && headcount > 0 && dur > 0) {
      spaceDetail = `(${formatGbp(perPerson)}/person/hr × ${headcount} people × ${Number(dur).toFixed(1)} hrs)`;
    } else if (hourly != null && hourly > 0 && dur > 0) {
      spaceDetail = `(${formatGbp(hourly)}/hr × ${Number(dur).toFixed(1)} hrs)`;
    }
  return {
    space_fee: roundMoney(spaceFee),
    add_ons: roundMoney(addOns),
    discount: roundMoney(discount),
    platform_fee: roundMoney(platform),
    processing_fee: roundMoney(processingFee),
    total_payable: roundMoney(totalPayable),
    space_detail: spaceDetail
  };
};

export const formatFeeBreakdownLines = (snapshot) => {
  if (!snapshot) return [];
  const lines = [
    snapshot.space_detail
      ? `Space fee ${formatGbp(snapshot.space_fee)} ${snapshot.space_detail}`
      : `Space fee ${formatGbp(snapshot.space_fee)}`
  ];
  if (Number(snapshot.add_ons) > 0) lines.push(`Add-ons ${formatGbp(snapshot.add_ons)}`);
  if (Number(snapshot.discount) > 0) lines.push(`Discount −${formatGbp(snapshot.discount)}`);
  if (Number(snapshot.platform_fee) > 0) lines.push(`Platform fee ${formatGbp(snapshot.platform_fee)}`);
  lines.push(`Processing fee ${formatGbp(snapshot.processing_fee)}`);
  lines.push(`Total payable ${formatGbp(snapshot.total_payable)}`);
  return lines;
};

/** Tooltip copy for fee info icons (aligned with Terms of Service). */
export const PLATFORM_FEE_INFO =
  "Occasia's platform fee covers booking and payment services on platform bookings. This fee is non-refundable.";

export const PROCESSING_FEE_INFO =
  'Estimated processing fee (2.9% + 30p) so the venue receives the full booking amount after Stripe charges. Actual fees may vary slightly. This fee is non-refundable.';
